import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faTrophy,
  faDownload,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons';
import './GameOver.css';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import { quizAccuracy } from './../../utils/shareUtils';
import Confetti from '../animations/Confetti';

interface FinalPlayer {
  rank: number;
  playerName: string;
  playerIcon: string;
  iconColor: string;
  score: number;
  correctCount: number;
  totalQuestions: number;
  avgTime: string;
}

interface QuestionAccuracy {
  label: string;
  questionSnippet: string;
  accuracy: number;
}

interface GameOverData {
  gameTitle: string;
  date: string;
  totalPlayers: number;
  totalQuestions: number;
  avgAccuracy: number;
  duration: string;
  players: FinalPlayer[];
  questionAccuracy: QuestionAccuracy[];
}

const mockGameOver: GameOverData = {
  gameTitle: 'World Capitals Quiz',
  date: '25 Mar 2026',
  totalPlayers: 7,
  totalQuestions: 10,
  avgAccuracy: 62,
  duration: '7 min',
  players: [
    {
      rank: 1,
      playerName: 'FoxRunner',
      playerIcon: '🦊',
      iconColor: '#FF6B6B',
      score: 10280,
      correctCount: 10,
      totalQuestions: 10,
      avgTime: '5.1s',
    },
    {
      rank: 2,
      playerName: 'WhaleRider',
      playerIcon: '🐋',
      iconColor: '#4ECDC4',
      score: 9240,
      correctCount: 10,
      totalQuestions: 10,
      avgTime: '6.3s',
    },
    {
      rank: 3,
      playerName: 'RocketGal',
      playerIcon: '🚀',
      iconColor: '#AB47BC',
      score: 8840,
      correctCount: 9,
      totalQuestions: 10,
      avgTime: '5.8s',
    },
    {
      rank: 4,
      playerName: 'GreenLeaf42',
      playerIcon: '🌿',
      iconColor: '#66BB6A',
      score: 7960,
      correctCount: 9,
      totalQuestions: 10,
      avgTime: '7.1s',
    },
    {
      rank: 5,
      playerName: 'LionKing',
      playerIcon: '🦁',
      iconColor: '#F9A825',
      score: 6800,
      correctCount: 8,
      totalQuestions: 10,
      avgTime: '6.5s',
    },
    {
      rank: 6,
      playerName: 'MagicHat99',
      playerIcon: '🎩',
      iconColor: '#AB47BC',
      score: 5940,
      correctCount: 7,
      totalQuestions: 10,
      avgTime: '10.2s',
    },
    {
      rank: 7,
      playerName: 'PetalDrop',
      playerIcon: '🌸',
      iconColor: '#FF6B6B',
      score: 4200,
      correctCount: 5,
      totalQuestions: 10,
      avgTime: '13.5s',
    },
  ],
  questionAccuracy: [
    { label: 'Q1', accuracy: 100, questionSnippet: 'What is the cap...' },
    { label: 'Q2', accuracy: 86, questionSnippet: 'Tokyo is the ca...' },
    { label: 'Q3', accuracy: 43, questionSnippet: 'Capital of Aust...' },
    { label: 'Q4', accuracy: 71, questionSnippet: 'The Nairobi is ...' },
    { label: 'Q5', accuracy: 29, questionSnippet: 'Capital of Braz...' },
    { label: 'Q6', accuracy: 86, questionSnippet: 'Capital of Germ...' },
    { label: 'Q7', accuracy: 57, questionSnippet: 'Capital of Cana...' },
    { label: 'Q8', accuracy: 100, questionSnippet: 'Capital of Spai...' },
    { label: 'Q9', accuracy: 43, questionSnippet: 'Capital of Arge...' },
    { label: 'Q10', accuracy: 14, questionSnippet: 'Capital of Kaza...' },
  ],
};

const podiumColors = ['#8B8B8B', '#D4A843', '#8B7355'];
const medalEmojis = ['🥈', '👑', '🥉'];

const getBarColor = (pct: number) => {
  if (pct >= 71) return 'var(--color-success)';
  if (pct >= 40) return 'var(--color-primary)';
  return 'var(--color-danger)';
};

const getRankBorder = (rank: number) => {
  if (rank === 1) return 'var(--color-primary)';
  if (rank === 2) return 'var(--color-primary)';
  if (rank === 3) return 'var(--color-primary)';
  return 'transparent';
};

interface Props {
  onPlayAgain: () => void;
  leaderBoard: LeaderboardEntry[];
  gameInfo: any;
}

const GameOver: React.FC<Props> = ({ onPlayAgain, leaderBoard, gameInfo }) => {
  const navigate = useNavigate();
  const { gameId } = useParams();

  const top3 = leaderBoard.slice(0, 3);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const totalPlayers = leaderBoard?.length;

  const totalTimeSpent = Math.max(
    ...(leaderBoard?.map((player) => player.totalTime) || [0])
  );
  const avgOverallAccuracy = Math.round(
    ((leaderBoard?.reduce((sum, player) => sum + player.correctCount, 0) || 0) /
      ((totalPlayers || 1) * (gameInfo.totalRound || 1))) *
      100
  );

  return (
    <div className="go">
      <Confetti active />
      {/* Left */}
      <div className="go__left">
        <button
          className="go__back"
          onClick={() => navigate(`/dashboard/games/game/${gameId}`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </button>

        <div className="go__hero">
          <h1 className="go__title">
            <FontAwesomeIcon icon={faTrophy} /> Game Over!
          </h1>
          <p className="go__subtitle">
            {gameInfo.gameTitle} {leaderBoard.length} players •{' '}
            {gameInfo.totalRound} questions
          </p>
        </div>

        {/* Podium */}
        <div className="go__podium">
          {podiumOrder.map((p, i) => {
            const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
            return (
              <div key={p.playerName} className="go__podium-col">
                <span className="go__podium-medal">{medalEmojis[i]}</span>
                <span
                  className="go__podium-avatar"
                  style={{
                    background: p.iconColor,
                    borderColor:
                      place === 1 ? 'var(--color-primary)' : p.iconColor,
                  }}
                >
                  {p.playerIcon}
                </span>
                <span className="go__podium-name">{p.playerName}</span>
                <div
                  className={`go__podium-block go__podium-block--${place}`}
                  style={{ background: podiumColors[i] }}
                >
                  <span className="go__podium-place">{place}</span>
                  <span className="go__podium-score">{p.totalPoints}</span>
                  {place === 1 && (
                    <span className="go__podium-label">CHAMPION</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Question Accuracy */}
        <div className="go__accuracy-card">
          <h3 className="go__accuracy-title">📊 Players-by-Accuracy</h3>
          <div className="go__accuracy-list">
            {leaderBoard.map((q, index) => (
              <div key={index} className="go__accuracy-row">
                <span className="go__accuracy-label">{index + 1}</span>
                <div className="go__accuracy-bar-wrap">
                  <div
                    className="go__accuracy-bar"
                    style={{
                      width: `${quizAccuracy(q, gameInfo.totalRound)}%`,
                      background: getBarColor(
                        quizAccuracy(q, gameInfo.totalRound)
                      ),
                    }}
                  >
                    <span className="go__accuracy-pct">
                      {quizAccuracy(q, gameInfo.totalRound)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="go__accuracy-legend">
            <span>
              <span
                className="go__legend-dot"
                style={{ background: 'var(--color-success)' }}
              />{' '}
              High (71-100%)
            </span>
            <span>
              <span
                className="go__legend-dot"
                style={{ background: 'var(--color-primary)' }}
              />{' '}
              Medium (40-70%)
            </span>
            <span>
              <span
                className="go__legend-dot"
                style={{ background: 'var(--color-danger)' }}
              />{' '}
              Low (&lt;40%)
            </span>
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="go__right">
        <h2 className="go__results-title">Final Results</h2>
        <p className="go__results-sub">{gameInfo.gameTitle}</p>

        <div className="go__stats">
          <div className="go__stat">
            <span className="go__stat-val">{totalPlayers}</span>
            <span className="go__stat-lbl">Players</span>
          </div>
          <div className="go__stat">
            <span className="go__stat-val">{avgOverallAccuracy}%</span>
            <span className="go__stat-lbl">Avg Accuracy</span>
          </div>
          <div className="go__stat">
            <span className="go__stat-val">
              {totalTimeSpent >= 60
                ? `${Math.floor(totalTimeSpent / 60)}m ${totalTimeSpent % 60}s`
                : `${totalTimeSpent}s`}
            </span>
            <span className="go__stat-lbl">Duration</span>
          </div>
        </div>

        <h4 className="go__rankings-title">Final Rankings</h4>
        <div className="go__rankings">
          {leaderBoard.map((p) => (
            <div
              key={p.playerName}
              className="go__rank-row"
              style={{
                borderColor:
                  p.rank <= 3
                    ? getRankBorder(p.rank)
                    : 'rgba(255,255,255,0.08)',
              }}
            >
              <span className="go__rank-num">#{p.rank}</span>
              <span
                className="go__rank-avatar"
                style={{ background: p.iconColor }}
              >
                {p.playerIcon}
              </span>
              <div className="go__rank-info">
                <span className="go__rank-name">{p.playerName}</span>
                <span className="go__rank-detail">
                  {Math.round(p.correctCount / gameInfo.totalRound)} correct •
                  avg {Math.round(p.totalTime / gameInfo.totalRound)}
                </span>
              </div>
              <span className="go__rank-score">{p.totalPoints}</span>
            </div>
          ))}
        </div>

        <button className="go__btn go__btn--primary" onClick={onPlayAgain}>
          🏆 Play Again — Same Settings
        </button>
      </div>
    </div>
  );
};

export default GameOver;
